import {Inject, Injectable} from '@angular/core';
import {OAuthEvent, OAuthService} from 'angular-oauth2-oidc';
import { HttpHeaders, HttpClient } from '@angular/common/http';
import {environment, CustomEnvironment, setApiOverrides} from '../../environments/environment';
import {Observable} from 'rxjs';
import {HttpErrorHandlerService, HandleError} from './http-error-handling.service';
import {JwtHelperService} from '@auth0/angular-jwt';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
    'Authorization': 'my-auth-token'
  })
};

@Injectable()
export class SalesService {
  private handleError: HandleError;
  private userDetails: any;
  public staffPermission = false;

  constructor(private oauthService: OAuthService,
              private http: HttpClient,
              httpErrorHandler: HttpErrorHandlerService,
              @Inject(CustomEnvironment) customEnvironment: any) {
    setApiOverrides(customEnvironment);

    this.oauthService.events.subscribe((event: OAuthEvent) => {
      const tokenUpdatedEvents: any = ['token_refreshed', 'token_received', 'silently_refreshed'];
      if (tokenUpdatedEvents.includes(event.type)) {
        httpOptions.headers = httpOptions.headers.set('Authorization', this.oauthService.authorizationHeader());
      }
    });

    this.handleError = httpErrorHandler.createHandleError('SalesService');
    this.checkIdentityClaims();
  }

  getOptions() {
    httpOptions.headers = httpOptions.headers.set('Authorization', this.oauthService.authorizationHeader());
    return httpOptions;
  }

  /**
   * Check user level permissions from the JWT token
   */
  public checkIdentityClaims() {
    this.userDetails = this.oauthService.getIdentityClaims();
    if (!this.userDetails) {
      const helper = new JwtHelperService();
      const rawToken = sessionStorage.getItem('access_token');
      const decodedToken = helper.decodeToken(rawToken);

      if (decodedToken && decodedToken.perms.split(',').find(x => x === 'search:view-staff')) {
        this.staffPermission = true;
      } else {
        this.staffPermission = false;
      }
    } else if (this.userDetails && this.userDetails.perms.split(',').find(x => x === 'search:view-staff')) {
      this.staffPermission = true;
    } else {
      this.staffPermission = false;
    }
  }

  /**
   * Update lead status
   * @param leadId the lead ID
   * @param statusSlug vx status slug
   * @param data status Body
   * @returns {Observable<A>}
   */
  updateLeadStatus(leadId, statusSlug, data) {
    return this.http.post<any[]>(`${environment.api.sales}/leads/${leadId}/status/${statusSlug}`, data, this.getOptions());
  }

  getUnattendedRequestId(id): Observable<any> {
    return this.http.get<any>(`${environment.api.sales}/2fa/status?requestId=${id}`, this.getOptions());
  }

  /**
   * Sends a fee validation request to the Sales API for the specified fee type.
   *
   * @param feeType - The type of fee being validated (e.g., "origination").
   * @param payload - The request body containing all required validation data.
   * @returns Observable containing the API response for the fee validation.
   *
   * Example payload:
   * {
   *   amount: number,
   *   fee: number,
   *   ...otherOptions
   * }
   */
  validateFee(feeType: string, payload: Record<any, any>) {
    return this.http.post(`${environment.api.sales}/fees/${feeType}/validate`, payload, this.getOptions());
  }
}
